import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, AlertTriangle, Plus, X, Lock, Unlock, Upload, FileText, BadgePoundSterling, ReceiptText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, {
  getQuote, getJobProfit, saveJobProfit, finaliseJobProfit, unlockJobProfit,
} from "@/lib/api";
import { useIsMobile } from "@/components/custom/AppShell";

const OPS_CATEGORIES = ["Parking", "Congestion Charge", "Tool Hire", "Skip Hire", "Waste Collection", "Fuel", "Other"];
const REJECT_REASONS = ["Not needed", "Client supplied", "Substituted", "Returned to supplier"];
const NO_RECEIPT_REASONS = ["Already in expenses", "Supplier invoice pending", "Cash purchase no receipt available"];

const uid = () => Math.random().toString(36).slice(2, 10);

function fmt(n) {
  const v = Number.isFinite(+n) ? +n : 0;
  return `£${v.toFixed(2)}`;
}

export default function JobProfitCalculator() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [quote, setQuote] = useState(null);
  const [items, setItems] = useState([]);
  const [media, setMedia] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [contractorRates, setContractorRates] = useState({});
  const [payRecords, setPayRecords] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [profit, setProfit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalising, setFinalising] = useState(false);

  // Form state
  const [labour, setLabour] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [opsExpenses, setOpsExpenses] = useState([]);
  const [lessonsLearned, setLessonsLearned] = useState("");

  // ───────────────────────────── load all data
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = await getQuote(id);
      // Active-Quote spec section 11: "The Job Profit Calculator should use the Active Quote
      // as the comparison baseline for 'quoted vs actual.'" If the user lands here on a
      // Superseded quote, we redirect to the family's Active so the baseline math is correct.
      if (q && q.selected_for_send === false) {
        try {
          const famRes = await api.get(`/quotes/${id}/family`);
          const activeMember = (famRes.data?.family || []).find(m => m.is_active);
          if (activeMember && activeMember.id !== id) {
            toast.info(`Profit baseline always uses the Active Quote (${activeMember.duplicate_label || "Option 1"}) — redirecting.`);
            navigate(`/quotes/${activeMember.id}/profit`, { replace: true });
            return;
          }
        } catch {
          // If the family lookup fails for any reason, fall through and use the current quote
          // (degraded state, but better than blocking the user).
        }
      }
      setQuote(q);
      setItems(q.items || []);
      setMedia(q.media || []);
      const [tm, cr, pr, exp, prof] = await Promise.all([
        api.get("/team-members").then(r => r.data).catch(() => []),
        api.get("/settings/contractor-rates").then(r => r.data?.rates || {}).catch(() => ({})),
        api.get("/pay-records").then(r => r.data).catch(() => []),
        api.get("/expenses").then(r => r.data).catch(() => []),
        getJobProfit(id).catch(() => null),
      ]);
      setTeamMembers(tm);
      setContractorRates(cr);
      setPayRecords(pr);
      setExpenses(exp);
      setProfit(prof);

      // Hydrate form sections from saved record, falling back to live data.
      if (prof && prof.labour) setLabour(prof.labour);
      else setLabour(buildDefaultLabourRows(q, tm, cr));
      if (prof && prof.materials) setMaterials(prof.materials);
      else setMaterials(buildDefaultMaterialRows(q.items || []));
      if (prof && prof.operating_expenses) setOpsExpenses(prof.operating_expenses);
      else setOpsExpenses(buildDefaultOpsRows(q, exp));
      if (prof && prof.lessons_learned) setLessonsLearned(prof.lessons_learned);
    } catch (err) {
      toast.error("Failed to load job profit data");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const locked = !!profit?.locked;

  // ───────────────────────────── derived totals (real time)
  const revenue = useMemo(() => {
    if (!quote) return { net_received: 0, invoice_total_ex_vat: 0, vat_amount: 0, commission_deducted: 0, source: "estimated" };
    const total = +(quote.total_inc_vat || 0);
    const exVat = +(total / 1.2).toFixed(2);
    const vat = +(total - exVat).toFixed(2);
    const isPaid = !!quote.invoice_paid;
    const netReceived = isPaid ? +(quote.invoice_paid_amount || 0) : exVat;
    const commission = (quote.shortfall_reason === "commission") ? +(quote.total_commission_deducted || 0) : 0;
    return {
      net_received: netReceived,
      invoice_total_inc_vat: total,
      invoice_total_ex_vat: exVat,
      vat_amount: vat,
      commission_deducted: commission,
      source: isPaid ? "payment_record" : "invoice_estimate",
    };
  }, [quote]);

  const labourConfirmed = useMemo(() => labour.filter(r => r.confirmed).reduce((s, r) => s + (+r.total_cost || 0), 0), [labour]);
  const labourUnconfirmed = useMemo(() => labour.filter(r => !r.confirmed).reduce((s, r) => s + (+r.total_cost || 0), 0), [labour]);

  const materialsActioned = useMemo(() => materials.filter(r => r.action === "confirm" || r.action === "adjust"), [materials]);
  const materialsConfirmedCost = useMemo(() => materialsActioned.filter(hasReceiptOrFlag).reduce((s, r) => s + (+r.total || 0), 0), [materialsActioned]);
  const materialsUnverifiedCost = useMemo(() => materialsActioned.filter(r => !hasReceiptOrFlag(r)).reduce((s, r) => s + (+r.total || 0), 0), [materialsActioned]);
  const materialsPending = useMemo(() => materials.filter(r => !r.action).length, [materials]);

  const opsTotal = useMemo(() => opsExpenses.reduce((s, e) => s + (+e.amount || 0), 0), [opsExpenses]);

  const grossProfit = useMemo(() => revenue.net_received - labourConfirmed - materialsConfirmedCost, [revenue, labourConfirmed, materialsConfirmedCost]);
  const netProfit = useMemo(() => grossProfit - opsTotal, [grossProfit, opsTotal]);
  const grossMargin = revenue.net_received > 0 ? (grossProfit / revenue.net_received) * 100 : 0;
  const netMargin = revenue.net_received > 0 ? (netProfit / revenue.net_received) * 100 : 0;

  const labourPending = labour.filter(r => !r.confirmed).length;
  // Require at least one labour row OR one material row (otherwise the calculation is
  // trivially empty). This blocks finalising a brand-new record with zero entries.
  const hasAnyEntry = labour.length > 0 || materials.length > 0;
  const allActioned = hasAnyEntry && labourPending === 0 && materialsPending === 0;

  // ───────────────────────────── save
  const persistDraft = useCallback(async () => {
    if (locked) return;
    setSaving(true);
    try {
      const user = localStorage.getItem("desktop_user_name") || "User";
      await saveJobProfit(id, {
        revenue,
        labour,
        materials,
        operating_expenses: opsExpenses,
        lessons_learned: lessonsLearned,
      }, user);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save draft");
    } finally {
      setSaving(false);
    }
  }, [id, locked, revenue, labour, materials, opsExpenses, lessonsLearned]);

  // Auto-save on changes (debounced)
  useEffect(() => {
    if (loading || locked) return;
    const t = setTimeout(persistDraft, 1500);
    return () => clearTimeout(t);
  }, [labour, materials, opsExpenses, lessonsLearned, loading, locked, persistDraft]);

  const handleFinalise = async () => {
    if (!allActioned) {
      toast.error("Confirm every labour row and action every material row before finalising");
      return;
    }
    setFinalising(true);
    try {
      const user = localStorage.getItem("desktop_user_name") || "User";
      await saveJobProfit(id, { revenue, labour, materials, operating_expenses: opsExpenses, lessons_learned: lessonsLearned }, user);
      await finaliseJobProfit(id, {
        labour_total: labourConfirmed,
        materials_total: materialsConfirmedCost,
        materials_unverified: materialsUnverifiedCost,
        operating_total: opsTotal,
        revenue: revenue.net_received,
        gross_profit: grossProfit,
        net_profit: netProfit,
        gross_margin: grossMargin,
        net_margin: netMargin,
      }, user);
      toast.success("Profit calculation finalised");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to finalise");
    } finally {
      setFinalising(false);
    }
  };

  const handleUnlock = async () => {
    setFinalising(true);
    try {
      const user = localStorage.getItem("desktop_user_name") || "User";
      await unlockJobProfit(id, user);
      toast.success("Profit calculation unlocked for editing");
      await load();
    } catch {
      toast.error("Failed to unlock");
    } finally {
      setFinalising(false);
    }
  };

  // ───────────────────────────── labour helpers
  const getRatesForMember = (memberId) => contractorRates[memberId] || {};
  const findPayRecordSuggestion = (memberId, fromDate, toDate) => {
    if (!memberId) return null;
    const start = fromDate ? new Date(fromDate).getTime() : 0;
    const end = toDate ? new Date(toDate).getTime() : Date.now();
    return payRecords.find(r => r.team_member_id === memberId && r.pay_date && (() => {
      const ts = new Date(r.pay_date).getTime();
      return ts >= start - 86400000 && ts <= end + 14 * 86400000;
    })());
  };
  const updateLabour = (rowId, patch) => setLabour(prev => prev.map(r => r.id === rowId ? { ...r, ...patch, total_cost: computeLabourTotal({ ...r, ...patch }) } : r));
  const addLabourRow = () => setLabour(prev => [...prev, { id: uid(), contractor_name: "", team_member_id: "", hours_or_days: 0, rate_type: "Hourly", rate_amount: 0, total_cost: 0, confirmed: false, source: "manual" }]);
  const removeLabourRow = (rowId) => setLabour(prev => prev.filter(r => r.id !== rowId));

  // ───────────────────────────── material helpers
  const updateMaterial = (rowId, patch) => setMaterials(prev => prev.map(r => r.id === rowId ? { ...r, ...patch, total: computeMaterialTotal({ ...r, ...patch }) } : r));
  const addMaterialRow = () => setMaterials(prev => [...prev, { id: uid(), source_item_id: "", supplier: "Toolstation", item_name: "", qty: 1, unit_price: 0, total: 0, quoted_qty: 0, quoted_unit_price: 0, quoted_total: 0, action: "confirm", receipt_url: "", receipt_not_available_reason: "" }]);
  const removeMaterialRow = (rowId) => setMaterials(prev => prev.filter(r => r.id !== rowId));

  const receiptSuggestions = useMemo(() => media.filter(m => m.category === "receipt" && !m.is_deleted), [media]);

  // ───────────────────────────── ops helpers
  const addOpsRow = () => setOpsExpenses(prev => [...prev, { id: uid(), category: "Parking", description: "", amount: 0, vat_amount: 0, receipt_url: "", source_expense_id: "", confirmed: true }]);
  const updateOpsRow = (rowId, patch) => setOpsExpenses(prev => prev.map(r => r.id === rowId ? { ...r, ...patch } : r));
  const removeOpsRow = (rowId) => setOpsExpenses(prev => prev.filter(r => r.id !== rowId));

  if (loading || !quote) return <div className="flex items-center justify-center h-screen" style={{ background: "var(--bg)" }}><p style={{ color: "var(--text-muted)" }}>Loading job profit calculator…</p></div>;

  const userJobInvoiced = ["Invoiced", "Partially Paid", "Paid", "Partially Refunded", "Refunded"].includes(quote.status);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="job-profit-calculator">
      {/* Top bar */}
      <div className="sticky top-0 z-20" style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
        <div className={`${isMobile ? "px-4 py-3" : "px-8 py-4"} flex items-center justify-between gap-3`}>
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(`/quotes/${id}`)} className="p-2 rounded-lg hover:bg-slate-100" data-testid="profit-back-btn"><ArrowLeft className="w-4 h-4" /></button>
            <div className="min-w-0">
              <h1 className="text-base font-semibold truncate" style={{ color: "var(--text-primary)" }}>Job Profit Calculator</h1>
              <p className="text-xs truncate" style={{ color: "var(--text-muted)" }}>{quote.site_address} · {quote.quote_number || ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {locked && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "#DCFCE7", color: "#166534" }} data-testid="profit-locked-badge"><Lock className="w-3 h-3" /> Calculated</span>
            )}
            {!userJobInvoiced && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}><AlertTriangle className="w-3 h-3" /> Job not yet invoiced</span>
            )}
            {saving && <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>Saving…</span>}
          </div>
        </div>
      </div>

      <div className={`${isMobile ? "px-4 py-4" : "px-8 py-6"} grid ${isMobile ? "grid-cols-1" : "grid-cols-[1fr_360px]"} gap-6`}>
        {/* Main */}
        <div className="space-y-6">
          {/* SECTION 1 — REVENUE */}
          <section className="ui-surface p-5" data-testid="profit-section-revenue">
            <SectionHeader title="Revenue" badge={<ConfirmedBadge />} />
            {revenue.source !== "payment_record" && (
              <div className="text-[11px] mt-1 mb-3 inline-flex items-center gap-1.5 px-2 py-1 rounded" style={{ background: "#FFFBEB", color: "#92400E" }}>
                <AlertTriangle className="w-3 h-3" /> Based on invoice total — update after payment is received to use actual net received.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mt-2 text-sm">
              <RevenueLine label="Invoice number" value={quote.quote_number || quote.invoice_number || "—"} mono />
              <RevenueLine label="Invoice date" value={quote.invoice_paid_date ? new Date(quote.invoice_paid_date).toLocaleDateString("en-GB") : (quote.updated_at ? new Date(quote.updated_at).toLocaleDateString("en-GB") : "—")} />
              <RevenueLine label="Invoice total inc VAT" value={fmt(revenue.invoice_total_inc_vat)} mono />
              <RevenueLine label="Invoice total ex VAT" value={fmt(revenue.invoice_total_ex_vat)} mono />
              <RevenueLine label="VAT amount" value={fmt(revenue.vat_amount)} mono />
              {revenue.commission_deducted > 0 && <RevenueLine label="Commission deducted" value={fmt(revenue.commission_deducted)} mono />}
              <RevenueLine label="Net revenue received" value={fmt(revenue.net_received)} mono accent />
            </div>
          </section>

          {/* SECTION 2 — LABOUR */}
          <section className="ui-surface p-5" data-testid="profit-section-labour">
            <SectionHeader title="Contractor Labour" subtitle="Confirm every row to include it in the profit calculation." />
            <div className="space-y-3 mt-3">
              {labour.length === 0 && <p className="text-xs" style={{ color: "var(--text-muted)" }}>No labour rows. Add manually if needed.</p>}
              {labour.map((row) => {
                const memberRates = getRatesForMember(row.team_member_id);
                const payRec = findPayRecordSuggestion(row.team_member_id, quote.start_date, quote.end_date);
                return (
                  <div key={row.id} className="rounded-lg p-3" style={{ border: `1px solid ${row.confirmed ? "#16a34a" : "var(--border)"}`, background: row.confirmed ? "#F0FDF4" : "var(--surface)" }} data-testid={`labour-row-${row.id}`}>
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-12 md:col-span-4">
                        <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Contractor</Label>
                        <Input
                          value={row.contractor_name}
                          onChange={(e) => updateLabour(row.id, { contractor_name: e.target.value })}
                          disabled={locked || row.confirmed}
                          className="mt-1 h-9 rounded-md text-sm"
                        />
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>{row.rate_type === "Daily" ? "Days" : "Hours"}</Label>
                        <Input type="number" step="0.25" value={row.hours_or_days} onChange={(e) => updateLabour(row.id, { hours_or_days: parseFloat(e.target.value) || 0 })} disabled={locked || row.confirmed} className="mt-1 h-9 rounded-md text-sm font-mono" />
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Rate</Label>
                        <select value={row.rate_type} onChange={(e) => updateLabour(row.id, { rate_type: e.target.value, rate_amount: e.target.value === "Daily" ? (memberRates.daily_rate || row.rate_amount) : (memberRates.hourly_rate || row.rate_amount) })} disabled={locked || row.confirmed} className="mt-1 h-9 rounded-md text-sm w-full px-2" style={{ border: "1px solid var(--border)" }}>
                          <option>Hourly</option>
                          <option>Daily</option>
                        </select>
                      </div>
                      <div className="col-span-6 md:col-span-2">
                        <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>£ / {row.rate_type === "Daily" ? "day" : "hour"}</Label>
                        <Input type="number" step="0.01" value={row.rate_amount} onChange={(e) => updateLabour(row.id, { rate_amount: parseFloat(e.target.value) || 0 })} disabled={locked || row.confirmed} className="mt-1 h-9 rounded-md text-sm font-mono" />
                      </div>
                      <div className="col-span-6 md:col-span-2 text-right">
                        <Label className="text-[10px] uppercase block" style={{ color: "var(--text-muted)" }}>Total</Label>
                        <p className="font-mono font-bold mt-1">{fmt(row.total_cost)}</p>
                      </div>
                    </div>
                    {row.booking_summary && (
                      <p className="text-[11px] mt-1.5" style={{ color: "var(--text-muted)" }}>Booked: {row.booking_summary}</p>
                    )}
                    {payRec && !row.confirmed && (
                      <div className="mt-2 p-2 rounded text-[11px] flex items-center justify-between gap-2" style={{ background: "#EFF6FF", color: "#1E40AF" }}>
                        <span>Pay record found — {row.contractor_name} paid £{(payRec.gross_pay || 0).toFixed(2)} ({payRec.pay_date}). Use this figure?</span>
                        <button onClick={() => updateLabour(row.id, { hours_or_days: parseFloat(payRec.days_worked || payRec.hours_worked || row.hours_or_days), rate_type: payRec.days_worked ? "Daily" : "Hourly", rate_amount: payRec.days_worked ? (memberRates.daily_rate || row.rate_amount) : (memberRates.hourly_rate || row.rate_amount) })} className="px-2 py-1 rounded font-semibold" style={{ background: "#1E40AF", color: "#fff" }}>Use</button>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      {row.confirmed
                        ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#15803D" }}><Check className="w-3.5 h-3.5" /> Confirmed</span>
                        : <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#D97706" }}><AlertTriangle className="w-3.5 h-3.5" /> Unconfirmed</span>}
                      <div className="flex gap-2">
                        {!locked && (
                          <>
                            {row.source === "manual" && <button onClick={() => removeLabourRow(row.id)} className="text-[11px] px-2 py-1 rounded text-red-600" data-testid={`labour-remove-${row.id}`}>Remove</button>}
                            <button onClick={() => updateLabour(row.id, { confirmed: !row.confirmed })} className="text-[11px] font-semibold px-3 py-1 rounded" style={row.confirmed ? { background: "var(--surface)", border: "1px solid var(--border)" } : { background: "#16a34a", color: "#fff" }} data-testid={`labour-confirm-${row.id}`}>{row.confirmed ? "Edit" : "Confirm"}</button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {!locked && (
                <button onClick={addLabourRow} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }} data-testid="labour-add-row"><Plus className="w-3.5 h-3.5" /> Add Contractor Row</button>
              )}
            </div>
            <div className="flex items-center justify-between pt-3 mt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <span className="text-sm font-semibold">Labour Total (confirmed)</span>
              <span className="font-mono font-bold text-lg" style={{ color: "var(--text-primary)" }}>{fmt(labourConfirmed)}</span>
            </div>
          </section>

          {/* SECTION 3 — MATERIALS */}
          <section className="ui-surface p-5" data-testid="profit-section-materials">
            <SectionHeader title="Materials Costs" subtitle="Action every quoted material row and upload a receipt (or mark unavailable) for confirmed lines." />
            <div className="space-y-3 mt-3">
              {materials.length === 0 && <p className="text-xs" style={{ color: "var(--text-muted)" }}>No materials on this quote. Add purchased items below.</p>}
              {materials.map((row) => {
                const verified = hasReceiptOrFlag(row);
                return (
                  <div key={row.id} className="rounded-lg p-3" style={{ border: `1px solid ${row.action === "confirm" ? "#16a34a" : row.action === "adjust" ? "#D97706" : row.action === "reject" ? "var(--text-muted)" : "var(--border)"}`, background: row.action === "confirm" ? "#F0FDF4" : row.action === "reject" ? "#F3F4F6" : "var(--surface)" }} data-testid={`material-row-${row.id}`}>
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-12 md:col-span-3">
                        <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Supplier</Label>
                        <Input value={row.supplier} onChange={(e) => updateMaterial(row.id, { supplier: e.target.value })} disabled={locked || row.action === "reject"} className="mt-1 h-9 rounded-md text-sm" />
                      </div>
                      <div className="col-span-12 md:col-span-3">
                        <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Item</Label>
                        <Input value={row.item_name} onChange={(e) => updateMaterial(row.id, { item_name: e.target.value })} disabled={locked || row.action === "reject"} className="mt-1 h-9 rounded-md text-sm" />
                      </div>
                      <div className="col-span-4 md:col-span-1">
                        <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Qty</Label>
                        <Input type="number" step="1" value={row.qty} onChange={(e) => updateMaterial(row.id, { qty: parseInt(e.target.value, 10) || 0 })} disabled={locked || row.action !== "adjust"} className="mt-1 h-9 rounded-md text-sm font-mono" />
                      </div>
                      <div className="col-span-4 md:col-span-2">
                        <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Unit £</Label>
                        <Input type="number" step="0.01" value={row.unit_price} onChange={(e) => updateMaterial(row.id, { unit_price: parseFloat(e.target.value) || 0 })} disabled={locked || row.action !== "adjust"} className="mt-1 h-9 rounded-md text-sm font-mono" />
                      </div>
                      <div className="col-span-4 md:col-span-2 text-right">
                        <Label className="text-[10px] uppercase block" style={{ color: "var(--text-muted)" }}>Total</Label>
                        <p className="font-mono font-bold mt-1">{fmt(row.total)}</p>
                      </div>
                      <div className="col-span-12 md:col-span-1 flex justify-end gap-1">
                        {!locked && row.source === "manual" && <button onClick={() => removeMaterialRow(row.id)} className="text-red-600 p-1" data-testid={`material-remove-${row.id}`}><X className="w-3.5 h-3.5" /></button>}
                      </div>
                    </div>
                    {row.quoted_total > 0 && (
                      <p className="text-[11px] mt-1.5" style={{ color: "var(--text-muted)" }}>Quoted: {row.quoted_qty} × {fmt(row.quoted_unit_price)} = {fmt(row.quoted_total)}</p>
                    )}
                    {/* Action selector */}
                    {!locked && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <ActionPill active={row.action === "confirm"} onClick={() => updateMaterial(row.id, { action: "confirm" })} testId={`material-action-confirm-${row.id}`}>Confirm as purchased</ActionPill>
                        <ActionPill active={row.action === "adjust"} onClick={() => updateMaterial(row.id, { action: "adjust" })} testId={`material-action-adjust-${row.id}`}>Adjust qty/price</ActionPill>
                        <ActionPill active={row.action === "reject"} onClick={() => updateMaterial(row.id, { action: "reject", total: 0 })} testId={`material-action-reject-${row.id}`}>Reject — not purchased</ActionPill>
                      </div>
                    )}
                    {/* Reject reason */}
                    {row.action === "reject" && (
                      <div className="mt-2">
                        <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Reason</Label>
                        <select value={row.reject_reason || ""} onChange={(e) => updateMaterial(row.id, { reject_reason: e.target.value })} disabled={locked} className="h-9 rounded-md text-sm w-full px-2 mt-1" style={{ border: "1px solid var(--border)" }}>
                          <option value="">Select reason…</option>
                          {REJECT_REASONS.map(r => <option key={r}>{r}</option>)}
                        </select>
                      </div>
                    )}
                    {/* Receipt for confirm/adjust */}
                    {(row.action === "confirm" || row.action === "adjust") && (
                      <ReceiptInline row={row} suggestions={receiptSuggestions} locked={locked} onUpdate={(patch) => updateMaterial(row.id, patch)} />
                    )}
                    {/* Pending warning */}
                    {!row.action && (
                      <p className="text-[11px] mt-2 inline-flex items-center gap-1 font-semibold" style={{ color: "#D97706" }}><AlertTriangle className="w-3.5 h-3.5" /> Pending — please select an action</p>
                    )}
                  </div>
                );
              })}
              {!locked && (
                <button onClick={addMaterialRow} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg" style={{ background: "var(--bg)", border: "1px solid var(--border)" }} data-testid="material-add-row"><Plus className="w-3.5 h-3.5" /> Add Material</button>
              )}
            </div>
            <div className="flex items-center justify-between pt-3 mt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <div>
                <p className="text-sm font-semibold">Materials Total (verified)</p>
                {materialsUnverifiedCost > 0 && <p className="text-[11px]" style={{ color: "#D97706" }}>Unverified (no receipt): {fmt(materialsUnverifiedCost)}</p>}
              </div>
              <span className="font-mono font-bold text-lg" style={{ color: "var(--text-primary)" }}>{fmt(materialsConfirmedCost)}</span>
            </div>
          </section>

          {/* SECTION 4 — OPERATING EXPENSES */}
          <section className="ui-surface p-5" data-testid="profit-section-ops">
            <SectionHeader title="Operating Expenses" subtitle="Parking, congestion, skip hire, tool hire, waste etc." />
            <div className="space-y-2 mt-3">
              {opsExpenses.length === 0 && <p className="text-xs" style={{ color: "var(--text-muted)" }}>No operating expenses logged. Add below if applicable.</p>}
              {opsExpenses.map((row) => (
                <div key={row.id} className="rounded-lg p-3 grid grid-cols-12 gap-2 items-end" style={{ border: "1px solid var(--border)", background: row.source_expense_id ? "#F8FAFC" : "var(--surface)" }} data-testid={`ops-row-${row.id}`}>
                  <div className="col-span-12 md:col-span-3">
                    <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Category</Label>
                    <select value={row.category} onChange={(e) => updateOpsRow(row.id, { category: e.target.value })} disabled={locked || !!row.source_expense_id} className="h-9 rounded-md text-sm w-full px-2 mt-1" style={{ border: "1px solid var(--border)" }}>
                      {OPS_CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-span-12 md:col-span-5">
                    <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Description</Label>
                    <Input value={row.description} onChange={(e) => updateOpsRow(row.id, { description: e.target.value })} disabled={locked || !!row.source_expense_id} className="mt-1 h-9 rounded-md text-sm" />
                  </div>
                  <div className="col-span-6 md:col-span-2">
                    <Label className="text-[10px] uppercase" style={{ color: "var(--text-muted)" }}>Amount</Label>
                    <Input type="number" step="0.01" value={row.amount} onChange={(e) => updateOpsRow(row.id, { amount: parseFloat(e.target.value) || 0 })} disabled={locked || !!row.source_expense_id} className="mt-1 h-9 rounded-md text-sm font-mono" />
                  </div>
                  <div className="col-span-6 md:col-span-2 flex justify-end items-center gap-2">
                    {row.source_expense_id && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>Linked</span>}
                    {!locked && !row.source_expense_id && <button onClick={() => removeOpsRow(row.id)} className="text-red-600"><X className="w-3.5 h-3.5" /></button>}
                  </div>
                </div>
              ))}
              {!locked && (
                <button onClick={addOpsRow} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg mt-2" style={{ background: "var(--bg)", border: "1px solid var(--border)" }} data-testid="ops-add-row"><Plus className="w-3.5 h-3.5" /> Add Operating Expense</button>
              )}
            </div>
            <div className="flex items-center justify-between pt-3 mt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <span className="text-sm font-semibold">Operating Expenses Total</span>
              <span className="font-mono font-bold text-lg" style={{ color: "var(--text-primary)" }}>{fmt(opsTotal)}</span>
            </div>
          </section>

          {/* COMPARISON */}
          {locked && profit?.summary && (
            <ComparisonPanel quote={quote} items={items} summary={profit.summary} netMargin={netMargin} />
          )}

          {/* LESSONS LEARNED */}
          <section className="ui-surface p-5">
            <SectionHeader title="Lessons learned (optional)" />
            <textarea value={lessonsLearned} onChange={(e) => setLessonsLearned(e.target.value)} disabled={locked} rows={3} placeholder="Note anything that affected profitability — unexpected complications, materials price changes, time overruns, quotes that were too low or too high. These notes inform future quoting." className="w-full mt-3 rounded-lg p-3 text-sm" style={{ border: "1px solid var(--border)", background: locked ? "var(--bg)" : "var(--surface)" }} data-testid="profit-lessons" />
          </section>
        </div>

        {/* Sticky sidebar */}
        <div>
          <div className={`${isMobile ? "fixed left-0 right-0 bottom-0 rounded-t-2xl shadow-2xl" : "sticky top-24"} ui-surface p-4`} style={{ background: "var(--surface)", zIndex: isMobile ? 30 : "auto", border: "1px solid var(--border)" }} data-testid="profit-sidebar">
            <h3 className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: "var(--text-muted)" }}>Profit Summary</h3>
            {!allActioned ? (
              <div className="mb-3 p-2 rounded text-[11px] font-semibold inline-flex items-center gap-1" style={{ background: "#FFFBEB", color: "#92400E" }} data-testid="profit-incomplete-badge">
                <AlertTriangle className="w-3 h-3" /> Incomplete — {labourPending + materialsPending} items pending
              </div>
            ) : (
              <div className="mb-3 p-2 rounded text-[11px] font-semibold inline-flex items-center gap-1" style={{ background: "#DCFCE7", color: "#166534" }} data-testid="profit-ready-badge">
                <Check className="w-3 h-3" /> All figures confirmed — ready to finalise
              </div>
            )}
            <SummaryLine label="Revenue" value={revenue.net_received} confirmed={revenue.source === "payment_record"} />
            <SummaryLine label="Labour costs" value={labourConfirmed} note={labourUnconfirmed > 0 ? `(£${labourUnconfirmed.toFixed(2)} unconfirmed)` : ""} sub />
            <SummaryLine label="Materials costs" value={materialsConfirmedCost} sub />
            <SummaryLine label="Operating expenses" value={opsTotal} sub />
            <div className="pt-3 mt-3" style={{ borderTop: "1px dashed var(--border)" }}>
              <SummaryLine label="Gross Profit" value={grossProfit} big positive={grossProfit >= 0} />
              <SummaryLine label="Net Profit" value={netProfit} big positive={netProfit >= 0} />
              <p className="text-[11px] mt-1 text-right" style={{ color: "var(--text-muted)" }}>Gross margin {grossMargin.toFixed(1)}% · Net margin {netMargin.toFixed(1)}%</p>
            </div>
            {!locked ? (
              <Button onClick={handleFinalise} disabled={!allActioned || finalising} className="w-full mt-3 gap-1.5" style={{ background: "#E05C2A", color: "#fff" }} data-testid="profit-finalise-btn">
                {finalising ? "Finalising…" : (<><BadgePoundSterling className="w-4 h-4" />Finalise Profit Calculation</>)}
              </Button>
            ) : (
              <Button onClick={handleUnlock} disabled={finalising} variant="outline" className="w-full mt-3 gap-1.5" data-testid="profit-unlock-btn">
                {finalising ? "Unlocking…" : (<><Unlock className="w-4 h-4" />Unlock to Edit</>)}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────── helpers + sub components

function SectionHeader({ title, subtitle, badge }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h2 className="text-sm font-semibold tracking-wide uppercase" style={{ color: "var(--text-secondary)" }}>{title}</h2>
        {subtitle && <p className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>{subtitle}</p>}
      </div>
      {badge}
    </div>
  );
}

function ConfirmedBadge() {
  return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#DCFCE7", color: "#166534" }}><Check className="w-3 h-3" /> Confirmed</span>;
}

function RevenueLine({ label, value, mono = false, accent = false }) {
  return (
    <div className="flex items-center justify-between text-sm" style={{ borderBottom: "1px dashed var(--border)", padding: "6px 0" }}>
      <span style={{ color: "var(--text-muted)" }}>{label}</span>
      <span className={`${mono ? "font-mono" : ""} ${accent ? "font-bold" : "font-medium"}`} style={{ color: accent ? "#E05C2A" : "var(--text-primary)" }}>{value}</span>
    </div>
  );
}

function ActionPill({ active, onClick, children, testId }) {
  return (
    <button type="button" onClick={onClick} className="text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{ border: active ? "2px solid #E05C2A" : "1px solid var(--border)", background: active ? "#FFF5F0" : "var(--surface)", color: active ? "#E05C2A" : "var(--text-secondary)" }} data-testid={testId}>
      {children}
    </button>
  );
}

function ReceiptInline({ row, suggestions, locked, onUpdate }) {
  const [showSuggestions, setShowSuggestions] = useState(suggestions.length > 0 && !row.receipt_url && !row.receipt_not_available_reason);
  const onFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Use existing media upload endpoint (object storage). For simplicity, store file name only.
    onUpdate({ receipt_url: file.name, receipt_uploaded_inline: true });
    toast.success(`Receipt attached: ${file.name}`);
  };
  return (
    <div className="mt-2 p-2 rounded" style={{ background: "var(--bg)", border: "1px solid var(--border)" }}>
      {row.receipt_url && (
        <div className="text-[11px] flex items-center gap-1.5" style={{ color: "#15803D" }}>
          <Check className="w-3 h-3" /> Receipt: <span className="font-mono">{row.receipt_url}</span>
          {!locked && <button onClick={() => onUpdate({ receipt_url: "" })} className="ml-2 text-red-600">remove</button>}
        </div>
      )}
      {!row.receipt_url && row.receipt_not_available_reason && (
        <div className="text-[11px] flex items-center gap-1.5" style={{ color: "#D97706" }} data-testid={`material-no-receipt-${row.id}`}>
          <AlertTriangle className="w-3 h-3" /> No Receipt — {row.receipt_not_available_reason}
          {!locked && <button onClick={() => onUpdate({ receipt_not_available_reason: "" })} className="ml-2 text-red-600">clear</button>}
        </div>
      )}
      {!row.receipt_url && !row.receipt_not_available_reason && !locked && (
        <div className="space-y-1.5">
          {showSuggestions && suggestions.length > 0 && (
            <div className="flex items-center gap-2 text-[11px] p-1.5 rounded" style={{ background: "#EFF6FF", color: "#1E40AF" }}>
              <ReceiptText className="w-3.5 h-3.5" />
              <span className="flex-1">Receipt already uploaded — {suggestions[0].file_name || "receipt"}. Use this receipt?</span>
              <button onClick={() => { onUpdate({ receipt_url: suggestions[0].file_name || suggestions[0].id, receipt_storage_path: suggestions[0].storage_path }); setShowSuggestions(false); }} className="px-2 py-0.5 rounded font-semibold" style={{ background: "#1E40AF", color: "#fff" }} data-testid={`material-use-receipt-${row.id}`}>Use</button>
              <button onClick={() => setShowSuggestions(false)} className="px-2 py-0.5 rounded font-semibold" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>Upload different</button>
            </div>
          )}
          <div className="flex items-center gap-2 text-[11px]">
            <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid={`material-upload-receipt-${row.id}`}>
              <Upload className="w-3 h-3" /> Upload receipt
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFileSelect} />
            </label>
            <span style={{ color: "var(--text-muted)" }}>or</span>
            <select onChange={(e) => onUpdate({ receipt_not_available_reason: e.target.value })} className="h-7 px-2 rounded text-[11px]" style={{ border: "1px solid var(--border)" }} defaultValue="" data-testid={`material-no-receipt-select-${row.id}`}>
              <option value="">Mark receipt not available…</option>
              {NO_RECEIPT_REASONS.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryLine({ label, value, confirmed, note, sub, big, positive }) {
  const color = big ? (positive ? "#16a34a" : "#DC2626") : "var(--text-primary)";
  return (
    <div className={`flex items-center justify-between ${sub ? "text-xs" : "text-sm"} py-1`}>
      <span style={{ color: sub ? "var(--text-muted)" : "var(--text-secondary)" }}>{label}{confirmed && <Check className="w-3 h-3 inline ml-1" style={{ color: "#16a34a" }} />}</span>
      <div className="text-right">
        <span className={`${big ? "text-base font-bold" : "font-semibold"} font-mono`} style={{ color }}>{fmt(value)}</span>
        {note && <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>{note}</p>}
      </div>
    </div>
  );
}

function ComparisonPanel({ quote, items, summary, netMargin }) {
  const quotedLabour = +(quote.labour_total || 0);
  const quotedMaterials = +(quote.materials_total || 0);
  const quotedOps = +(quote.parking_total || 0) + +(quote.congestion_total || 0);
  const quotedTotalCost = quotedLabour + quotedMaterials + quotedOps;
  const quotedRevenue = +(quote.total_inc_vat || 0) / 1.2;
  const quotedGross = quotedRevenue - quotedLabour - quotedMaterials;
  const quotedNet = quotedGross - quotedOps;

  const rows = [
    { label: "Labour", quoted: quotedLabour, actual: summary.labour_total, lowerBetter: true },
    { label: "Materials", quoted: quotedMaterials, actual: summary.materials_total, lowerBetter: true },
    { label: "Operating Expenses", quoted: quotedOps, actual: summary.operating_total, lowerBetter: true },
    { label: "Total Costs", quoted: quotedTotalCost, actual: (summary.labour_total + summary.materials_total + summary.operating_total), lowerBetter: true },
    { label: "Revenue", quoted: quotedRevenue, actual: summary.revenue, lowerBetter: false },
    { label: "Gross Profit", quoted: quotedGross, actual: summary.gross_profit, lowerBetter: false },
    { label: "Net Profit", quoted: quotedNet, actual: summary.net_profit, lowerBetter: false },
  ];
  const totalCostsRow = rows.find(r => r.label === "Total Costs");
  const variance = (totalCostsRow.quoted - totalCostsRow.actual);
  return (
    <section className="ui-surface p-5" data-testid="profit-comparison-panel">
      <SectionHeader title="Quoted vs Actual" />
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr style={{ borderBottom: "1px solid var(--border)" }}>
            <th className="text-left py-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Category</th>
            <th className="text-right py-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Quoted</th>
            <th className="text-right py-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Actual</th>
            <th className="text-right py-2 text-[10px] uppercase font-bold" style={{ color: "var(--text-muted)" }}>Variance</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const diff = r.actual - r.quoted;
              const isBetter = r.lowerBetter ? diff < 0 : diff > 0;
              const color = Math.abs(diff) < 0.01 ? "var(--text-primary)" : (isBetter ? "#16a34a" : "#DC2626");
              return (
                <tr key={r.label} style={{ borderBottom: "1px dashed var(--border)" }}>
                  <td className="py-2 font-medium">{r.label}</td>
                  <td className="py-2 text-right font-mono">{fmt(r.quoted)}</td>
                  <td className="py-2 text-right font-mono">{fmt(r.actual)}</td>
                  <td className="py-2 text-right font-mono font-semibold" style={{ color }}>{(diff >= 0 ? "+" : "") + fmt(diff).replace("£-", "-£")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs mt-3 text-center" style={{ color: "var(--text-secondary)" }}>This job came in <strong>{variance >= 0 ? fmt(Math.abs(variance)) + " under" : fmt(Math.abs(variance)) + " over"}</strong> budget on a <strong>{netMargin.toFixed(1)}%</strong> net margin.</p>
    </section>
  );
}

// ─────────────────────────────────────────── default rows
function buildDefaultLabourRows(quote, teamMembers, contractorRates) {
  const rows = [];
  const seen = new Set();
  const ratesFor = (id) => contractorRates[id] || {};
  const summary = (start, end) => start ? `${start}${end && end !== start ? " to " + end : ""}` : "";
  const lead = quote.booked_team_member_id;
  const leadName = quote.booked_team_member_name;
  if (lead) {
    const r = ratesFor(lead);
    rows.push({
      id: uid(), team_member_id: lead, contractor_name: leadName || "",
      hours_or_days: parseFloat(quote.booked_duration || 1),
      rate_type: r.daily_rate ? "Daily" : "Hourly",
      rate_amount: r.daily_rate || r.hourly_rate || 0,
      total_cost: 0,
      confirmed: false,
      source: "booking",
      booking_summary: summary(quote.start_date, quote.end_date),
    });
    rows[0].total_cost = computeLabourTotal(rows[0]);
    seen.add(lead);
  }
  for (const am of (quote.additional_members || [])) {
    if (am.team_member_id && !seen.has(am.team_member_id)) {
      const r = ratesFor(am.team_member_id);
      const m = teamMembers.find(t => t.id === am.team_member_id);
      const row = {
        id: uid(), team_member_id: am.team_member_id, contractor_name: m?.name || "",
        hours_or_days: 1,
        rate_type: r.daily_rate ? "Daily" : "Hourly",
        rate_amount: r.daily_rate || r.hourly_rate || 0,
        total_cost: 0, confirmed: false, source: "booking",
        booking_summary: summary(am.start_date, am.end_date),
      };
      row.total_cost = computeLabourTotal(row);
      rows.push(row);
      seen.add(am.team_member_id);
    }
  }
  return rows;
}

function buildDefaultMaterialRows(items) {
  return (items || []).map((it) => ({
    id: uid(),
    source_item_id: it.id,
    supplier: it.supplier || "",
    item_name: it.item_name || "",
    qty: it.qty || 1,
    unit_price: +(it.unit_price || 0),
    total: (it.qty || 1) * +(it.unit_price || 0),
    quoted_qty: it.qty || 1,
    quoted_unit_price: +(it.unit_price || 0),
    quoted_total: (it.qty || 1) * +(it.unit_price || 0),
    action: "",
    receipt_url: "",
    receipt_not_available_reason: "",
    source: "quote",
  }));
}

function buildDefaultOpsRows(quote, expenses) {
  const linked = (expenses || []).filter(e => e.quote_id === quote.id).map(e => ({
    id: uid(), source_expense_id: e.id, category: e.category || "Other",
    description: e.description || e.merchant || "", amount: +(e.amount || 0), vat_amount: +(e.vat_amount || 0),
    receipt_url: "", confirmed: !!e.confirmed,
  }));
  return linked;
}

function computeLabourTotal(r) {
  return +(((+r.hours_or_days) || 0) * ((+r.rate_amount) || 0)).toFixed(2);
}
function computeMaterialTotal(r) {
  if (r.action === "reject") return 0;
  return +(((+r.qty) || 0) * ((+r.unit_price) || 0)).toFixed(2);
}
function hasReceiptOrFlag(r) {
  return !!(r.receipt_url || r.receipt_not_available_reason);
}
